import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Activity, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { ROUTES } from '@/lib/routes';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? ROUTES.DASHBOARD;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user) return <Navigate to={from} replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Erreur réseau';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-dark via-[#1e3a2e] to-[#2a4a3a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-brand bg-brand-primary mb-4 shadow-lg">
            <Activity size={26} className="text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white">Dispatcher</h1>
          <p className="text-green-200/60 text-sm mt-1">Console d'orchestration</p>
        </div>

        <div className="card p-8 shadow-xl">
          <h2 className="font-heading text-lg font-bold text-brand-dark mb-6">Connexion</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="vous@exemple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
              />
            </div>

            <div>
              <label className="label" htmlFor="password">Mot de passe</label>
              <input
                id="password"
                type="password"
                className="input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div role="alert" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-brand px-3 py-2">
                {error}
              </div>
            )}

            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading && <Loader2 size={14} className="animate-spin" />}
              Se connecter
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-4">
            <Link to={ROUTES.FORGOT_PASSWORD} className="hover:text-brand-primary transition-colors">
              Mot de passe oublié ?
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-green-200/40 mt-6">JWT · refresh token rotation</p>
      </div>
    </div>
  );
}
